<script lang="ts" setup>
import { ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosApiClient from '@/axios.ts';
import { QuillEditor } from '@vueup/vue-quill';

interface Config {
  id: number;
  key: string;
  value: string;
  type: string;
  description: string;
}
const configsList = ref<Config[]>([
  {
    id: 0,
    key: 'corp_name',
    value: '',
    type: 'general',
    description: 'Name',
  },
  {
    id: 0,
    key: 'corp_phone',
    value: '',
    type: 'general',
    description: 'Phone',
  },
  {
    id: 0,
    key: 'corp_mobile',
    value: '',
    type: 'general',
    description: 'Mobile',
  },
  {
    id: 0,
    key: 'corp_email',
    value: '',
    type: 'general',
    description: 'Email',
  },
  {
    id: 0,
    key: 'corp_address',
    value: '',
    type: 'general',
    description: 'Address',
  },
  {
    id: 0,
    key: 'corp_description',
    value: '',
    type: 'general',
    description: 'Description',
  },
  {
    id: 0,
    key: 'corp_footer_text',
    value: '',
    type: 'general',
    description: 'Footer Text',
  },
  {
    id: 0,
    key: 'meta_title',
    value: '',
    type: 'general',
    description: 'Meta Title',
  },
  {
    id: 0,
    key: 'meta_description',
    value: '',
    type: 'general',
    description: 'Meta Description',
  },
  {
    id: 0,
    key: 'meta_keywords',
    value: '',
    type: 'general',
    description: 'Meta Keywords',
  },
  {
    id: 0,
    key: 'meta_author',
    value: '',
    type: 'general',
    description: 'Meta Author',
  },
  {
    id: 0,
    key: 'corp_logo',
    value: '',
    type: 'media',
    description: 'Logo',
  },
  {
    id: 0,
    key: 'corp_favicon',
    value: '',
    type: 'media',
    description: 'Favicon',
  },
  {
    id: 0,
    key: 'social_instagram_link',
    value: '',
    type: 'social',
    description: 'Instagram Link',
  },
  {
    id: 0,
    key: 'social_instagram',
    value: '',
    type: 'social',
    description: 'Instagram',
  },
  {
    id: 0,
    key: 'social_facebook',
    value: '',
    type: 'social',
    description: 'Facebook',
  },
  {
    id: 0,
    key: 'social_twitter',
    value: '',
    type: 'social',
    description: 'Twitter',
  },
  {
    id: 0,
    key: 'social_linkedin',
    value: '',
    type: 'social',
    description: 'LinkedIn',
  },
  {
    id: 0,
    key: 'social_youtube',
    value: '',
    type: 'social',
    description: 'YouTube',
  },
  {
    id: 0,
    key: 'social_whatsapp',
    value: '',
    type: 'social',
    description: 'WhatsApp',
  },
  {
    id: 0,
    key: 'social_snapchat',
    value: '',
    type: 'social',
    description: 'Snapchat',
  },
  {
    id: 0,
    key: 'social_tiktok',
    value: '',
    type: 'social',
    description: 'TikTok',
  },
  {
    id: 0,
    key: 'interior_design',
    value: '',
    type: 'stats',
    description: 'house interior design',
  },
  {
    id: 0,
    key: 'finished_projects',
    value: '',
    type: 'stats',
    description: 'finished projects',
  },
  {
    id: 0,
    key: 'expert_designer',
    value: '',
    type: 'stats',
    description: 'expert designer',
  },
]);
const mission = ref('');
const vision = ref('');
const message = ref('');
const isbusy = ref(false);
const t = useI18n().t;
const swal = inject('$swal') as any;

//general configs is an array of general corp props like name , phone , ...
const saveGeneralConfigs = async () => {
  isbusy.value = true;
  await axiosApiClient
    .get('/sanctum/csrf-cookie')
    .then(async () => {
      //loop over the configsList
      let saveErrors = [];
      configsList.value.forEach(async config => {
        setTimeout(() => {
          axiosApiClient
            .post('/api/configurations', config)
            .then(response => {
              if (response.status !== 200) {
                saveErrors.push(response.data.message);
              }
            })
            .catch(error => {
              swal.fire({
                icon: 'error',
                title: t('Oops...'),
                position: 'top-end',
                toast: true,
                text: t('Error while saving configurations!') + error.message,
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true,
              });
            })
            .finally(() => { });
        }, 100);       
      });
       //save mission ,vision and message
        await saveMission();
        await saveVision();
        await saveMessage();

      swal.fire({
        icon: 'success',
        title: t('Success!'),
        position: 'top-end',
        toast: true,
        text: t('Configurations saved successfully!'),
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });
      //wait for all the requests to finish
    })
    .catch(error => {
      console.error(error);
    })
    .finally(() => {
      setTimeout(() => {
        isbusy.value = false;
      }, 4000);
    });
};
const saveMission = async () => {
  await axiosApiClient
    .post('/api/configurations', {
      id: 0,
      key: 'mission',
      value: mission.value,
      type: 'general',
      description: 'Mission',
    })
    .then(response => {
      if (response.status !== 200) {
        swal.fire({
          icon: 'error',
          title: t('Oops...'),
          position: 'top-end',
          toast: true,
          text: t('Error while saving configurations!') + response.data.message,
          showConfirmButton: false,
          timer: 7500,
          timerProgressBar: true,
        });
      }
    })
    .catch(error => {
      console.error(error);
    })
    .finally(() => {
    });
};
const saveVision = async () => {
  await axiosApiClient
    .post('/api/configurations', {
      id: 0,
      key: 'vision',
      value: vision.value,
      type: 'general',
      description: 'Vision',
    })
    .then(response => {
      if (response.status !== 200) {
        swal.fire({
          icon: 'error',
          title: t('Oops...'),
          position: 'top-end',
          toast: true,
          text: t('Error while saving configurations!') + response.data.message,
          showConfirmButton: false,
          timer: 7500,
          timerProgressBar: true,
        });
      }
    })
    .catch(error => {
      console.error(error);
    })
    .finally(() => {
    });
};
const saveMessage = async () => {
  await axiosApiClient
    .post('/api/configurations', {
      id: 0,
      key: 'message',
      value: message.value,
      type: 'general',
      description: 'Message',
    })
    .then(response => {
      if (response.status !== 200) {
        swal.fire({
          icon: 'error',
          title: t('Oops...'),
          position: 'top-end',
          toast: true,
          text: t('Error while saving configurations!') + response.data.message,
          showConfirmButton: false,
          timer: 7500,
          timerProgressBar: true,
        });
      }
    })
    .catch(error => {
      console.error(error);
    })
    .finally(() => {
    });
};

const setLogo = async (event: any) => {
  //check file size
  if (event.target.files[0].size > 1024 * 1024 * 1) {
    return swal
      .fire({
        icon: 'error',
        title: t('File size should be less than 1MB'),
        showConfirmButton: false,
        timer: 19500,
        timerProgressBar: true,
        footer: `
            <div style="width: 100%;text-align: center; font-size: 14px; color: #000;">
            ".webp" ${t('Format Images is Preffered')}
            <br/>
            ${t('For More Info.')}
            <br/>
            ${t('Convert your images format')}
            <br/>
            <a href="https://image.online-convert.com/convert-to-webp">${t(
          'Images Converter',
        )}</a>
            </div>`,
      })
      .then(() => {
        isbusy.value = false;
      });
  }
  isbusy.value = true;
  const files = event.target.files;
  const reader = new FileReader();
  reader.readAsDataURL(files[0]);
  reader.onload = async () => {
    const base64String = reader.result as string;
    let config = {
      id: 0,
      key: 'corp_logo',
      value: base64String,
      type: 'general',
      description: 'Main Logo',
    };
    try {
      axiosApiClient
        .post('/api/configurations', config)
        .then(response => {
          console.log(response);
          if (response.status !== 200) {
          }
        })
        .catch(error => {
          swal.fire({
            icon: 'error',
            title: t('Oops...'),
            position: 'top-end',
            toast: true,
            text: t('Error while saving configurations!') + error.message,
            showConfirmButton: false,
            timer: 8500,
            timerProgressBar: true,
          });
        })
        .finally(() => { });
    } catch (error) {
      console.error(error);
    }
    isbusy.value = false;
  };
};
const setFavicon = async (event: any) => {
  //check file size
  if (event.target.files[0].size > 1024 * 1024 * 1) {
    return swal
      .fire({
        icon: 'error',
        title: t('File size should be less than 1MB'),
        showConfirmButton: false,
        timer: 19500,
        timerProgressBar: true,
        footer: `
            <div style="width: 100%;text-align: center; font-size: 14px; color: #000;">
            ".webp" ${t('Format Images is Preffered')}
            <br/>
            ${t('For More Info.')}
            <br/>
            ${t('Convert your images format')}
            <br/>
            <a href="https://image.online-convert.com/convert-to-webp">${t(
          'Images Converter',
        )}</a>
            </div>`,
      })
      .then(() => {
        isbusy.value = false;
      });
  }
  isbusy.value = true;
  const files = event.target.files;
  const reader = new FileReader();
  reader.readAsDataURL(files[0]);
  reader.onload = async () => {
    const base64String = reader.result as string;
    let config = {
      id: 0,
      key: 'corp_favicon',
      value: base64String,
      type: 'general',
      description: 'Favicon',
    };
    try {
      axiosApiClient
        .post('/api/configurations', config)
        .then(response => {
          console.log(response);
          if (response.status !== 200) {
          }
        })
        .catch(error => {
          swal.fire({
            icon: 'error',
            title: t('Oops...'),
            position: 'top-end',
            toast: true,
            text: t('Error while saving configurations!') + error.message,
            showConfirmButton: false,
            timer: 8500,
            timerProgressBar: true,
          });
        })
        .finally(() => { });
    } catch (error) {
      console.error(error);
    }
    isbusy.value = false;
  };
};
const loadGeneralConfigurations = async () => {
  isbusy.value = true;
  await axiosApiClient
    .get('/api/configurations/all')
    .then(async response => {
      if (response.status !== 200) {
        swal.fire({
          icon: 'error',
          title: t('Oops...'),
          position: 'top-end',
          toast: true,
          text: t('Error while loading configurations!'),
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
      } else {
        //return;
        //loop over the configsList and set the value from the configurations
        const db_general_configs = response.data;
        configsList.value.forEach(config => {
          db_general_configs.forEach((db_config: any) => {
            if (config.key === db_config.key) {
              config.value = db_config.value;
            }
          });
        });
        //load other configs
        //mission
        await loadConfig('mission');
        //vision
        await loadConfig('vision');
        //message
        await loadConfig('message');
      }
      isbusy.value = false;
    })
    .catch(error => {
      console.error(error);
    });
};
loadGeneralConfigurations();
const loadConfig = async (key: string) => {
  try {
    await axiosApiClient.get('/sanctum/csrf-cookie');
    let response = await await axiosApiClient.get('/api/configurations/' + key + '/by-key');
    if (response.status !== 200) {
      isbusy.value = false;
      return;
    }
    if (response.status !== 200) {
      swal.fire({
        icon: 'error',
        title: t('Oops...'),
        position: 'top-end',
        toast: true,
        text: t('Error while loading configurations!'),
        showConfirmButton: false,
        timer: 7500,
        timerProgressBar: true,
      });
    } else {

      if (key === 'mission') {
        mission.value = response.data.value;
      } else if (key === 'vision') {
        vision.value = response.data.value;
      } else if (key === 'message') {
        message.value = response.data.value;
      }
    }
  } catch (error) {
  } finally {
  }

};
</script>
<template>
  <admin-layout>
    <div class="flex justify-between items-center mb-4">
      <h3>{{ t('Configurations') }}</h3>
    </div>
    <div>
      <!--general config-->
      <label for="">{{ t('Corp Info.') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <div class="flex flex-row flex-wrap gap-2">
          <div v-for="(config, index) in configsList.filter(
            c => c.type == 'general',
          )" :key="index" class="flex-grow min-w-0.5 w-full md:w-1/2 lg:w-1/3">
            <div class="flex flex-col flex-grow">
              <label :for="config.key">
                {{ t(config.description || config.key) }}
              </label>
              <input type="text" :id="config.key" v-model="config.value" class="form-control w-full" />
            </div>
          </div>
        </div>
      </div>
      <!--End general config-->
      <!--media config-->
      <label for="media" class="block text-gray-700">
        {{ t('Media') }}
      </label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <div class="mb-4">
          <label for="">{{ t('Logo') }}</label>
          <input type="file" id="media" class="border rounded w-full py-2 px-3" @change="setLogo" />
        </div>
        <div class="mb-4">
          <label for="">{{ t('Favicon') }}</label>
          <input type="file" id="media" class="border rounded w-full py-2 px-3" @change="setFavicon" />
        </div>
      </div>
      <!--End media config-->
      <!--social config-->
      <label for="">{{ t('Social Media') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <div class="flex flex-row flex-wrap gap-2">
          <div v-for="(config, index) in configsList.filter(
            c => c.type == 'social',
          )" :key="index" class="flex-grow min-w-0.5 w-full md:w-1/2 lg:w-1/3">
            <div class="flex flex-col flex-grow">
              <label :for="config.key">
                {{ t(config.description || config.key) }}
              </label>
              <input type="text" :id="config.key" v-model="config.value" class="form-control w-full" />
            </div>
          </div>
        </div>
      </div>
      <!--End social config-->
      <!--stats config-->
      <label for="">{{ t('Statistics') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <div class="flex flex-row flex-wrap gap-2">
          <div v-for="(config, index) in configsList.filter(
            c => c.type == 'stats',
          )" :key="index" class="flex-grow min-w-0.5 w-full md:w-1/2 lg:w-1/3">
            <div class="flex flex-col flex-grow">
              <label :for="config.key">
                {{ t(config.description || config.key) }}
              </label>
              <input type="text" :id="config.key" v-model="config.value" class="form-control w-full" />
            </div>
          </div>
        </div>
      </div>
      <!--End stats config-->
      <!-- Mission config-->
      <label for="">{{ t('Our Mission') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <QuillEditor v-model:content="mission" content-type="html" toolbar="full" />
      </div>
      <!--End Mission config-->
      <!-- Vision config-->
      <label for="">{{ t('Our Vision') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <QuillEditor v-model:content="vision" content-type="html" toolbar="full" />
      </div>
      <!--End Vision config-->
      <!-- Our message config-->
      <label for="">{{ t('Our Message') }}</label>
      <div class="flex flex-col gap-2 w-full border rounded-md p-4 mb-4 bg-white shadow-md">
        <QuillEditor v-model:content="message" content-type="html" toolbar="full" />
      </div>
      <!--End Our message config-->
      <div class="flex justify-end mt-4">
        <button type="button" @click="saveGeneralConfigs" class="btn btn-primary" :disabled="isbusy">
          {{ isbusy ? t('Saving...') : t('Save') }}
        </button>
      </div>
    </div>
    <loading-spinner :is-loading="isbusy" :position="'fixed'" />
  </admin-layout>
</template>

<style lang="scss" scoped></style>
